/**
 * ReadOnlyTiptapViewer Component
 *
 * Purpose: Renders Tiptap JSON content in a read-only view without dangerouslySetInnerHTML.
 * Falls back to sanitized HTML rendering for legacy data.
 *
 * Features:
 * - Renders Tiptap JSON via read-only editor instance (no raw HTML parsing)
 * - Falls back to DOMPurify-sanitized HTML for legacy records
 * - Forwards refs for scroll detection (e.g., "must scroll to accept" flows)
 * - Same extensions as TiptapEditor for full node-type parity
 */

import * as React from 'react';
import { JSONContent, AnyExtension } from '@tiptap/core';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import DOMPurify from 'dompurify';
import { cn } from '../lib/utils';

export interface ReadOnlyTiptapViewerProps {
  /** Tiptap JSON document content. Takes precedence over htmlFallback. */
  content?: JSONContent | null;
  /** Legacy HTML string rendered as sanitized fallback when content is null. */
  htmlFallback?: string | null;
  className?: string;
  /**
   * Minimum height in pixels for the content area.
   * Must be a trusted numeric value — not sourced from user input.
   */
  minHeight?: number;
}

const VIEWER_EXTENSIONS: AnyExtension[] = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3, 4],
    },
  }),
  Underline,
  TextStyle,
  Color,
  Highlight.configure({
    multicolor: true,
  }),
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      rel: 'noopener noreferrer',
      target: '_blank',
    },
  }),
  Image.configure({
    inline: true,
    allowBase64: true,
  }),
  Table.configure({
    resizable: false,
  }),
  TableRow,
  TableCell,
  TableHeader,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
];

const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'b', 'p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li',
    'a', 'blockquote', 'code', 'pre', 'hr', 'i', 'div', 'span',
    's', 'strike', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'img', 'colgroup', 'col', 'mark',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'class', 'style', 'data-*',
    'colspan', 'rowspan', 'src', 'alt', 'title', 'width', 'height',
  ],
  ALLOW_DATA_ATTR: true,
};

/**
 * A read-only Tiptap viewer that renders JSON content without dangerouslySetInnerHTML.
 * Falls back to sanitized HTML for legacy records.
 *
 * The component forwards its ref to the outer wrapper div so callers can attach
 * scroll listeners (e.g., "must scroll to accept" patterns in ContractTermsReview).
 */
export const ReadOnlyTiptapViewer = React.forwardRef<
  HTMLDivElement,
  ReadOnlyTiptapViewerProps
>(({ content, htmlFallback, className, minHeight }, ref) => {
  const hasJsonContent = content != null;

  const editor = useEditor(
    {
      extensions: VIEWER_EXTENSIONS,
      content: hasJsonContent ? content : null,
      editable: false,
      editorProps: {
        attributes: {
          class: cn(
            'prose prose-sm dark:prose-invert max-w-none focus:outline-none p-4',
            minHeight && 'min-h-[var(--viewer-min-height)]',
          ),
          ...(minHeight
            ? { style: `--viewer-min-height: ${minHeight}px` }
            : {}),
        },
      },
    },
    [hasJsonContent],
  );

  // Keep content in sync when the JSON prop changes
  React.useEffect(() => {
    if (editor && hasJsonContent && content) {
      editor.commands.setContent(content);
    }
  }, [editor, content, hasJsonContent]);

  if (hasJsonContent) {
    return (
      <div ref={ref} className={className}>
        <EditorContent editor={editor} />
      </div>
    );
  }

  // Legacy HTML fallback — sanitized via DOMPurify.
  // This path exists for backward compatibility while data is being migrated from
  // TEXT columns to the new JSONB columns. Once all records have JSON content,
  // the htmlFallback prop and this code path can be removed.
  const sanitizedHtml = htmlFallback
    ? DOMPurify.sanitize(htmlFallback, SANITIZE_OPTIONS)
    : null;

  return (
    <div
      ref={ref}
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none p-4',
        className,
      )}
      dangerouslySetInnerHTML={
        sanitizedHtml != null ? { __html: sanitizedHtml } : undefined
      }
    />
  );
});

ReadOnlyTiptapViewer.displayName = 'ReadOnlyTiptapViewer';
